import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useEduAuth } from '../../context/EduAuthContext';
import { Mail, Lock, ArrowRight, RefreshCw, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/funnel`;

export default function EduLogin() {
  const navigate = useNavigate();
  const { eduLogin } = useEduAuth();

  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleEmail = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/capture`, { email: email.trim() });
      setStep('otp');
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/verify`, { email: email.trim(), otp: otp.trim() });
      eduLogin(data.token, data.email);
      navigate('/edu', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    try {
      await axios.post(`${API}/resend`, { email: email.trim() });
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-600/8 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500/15 to-purple-600/15 border border-purple-500/20 rounded-full px-4 py-1.5 mb-4">
            <span className="text-xs font-semibold text-purple-300 uppercase tracking-wider">edu.ruonn.com</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Sign in</h1>
          <p className="text-sm text-gray-400">
            {step === 'email'
              ? 'Enter your email — we\'ll send you a one-time code'
              : `Code sent to ${email}`}
          </p>
        </div>

        <div className="bg-[#111115] border border-white/8 rounded-2xl p-6 shadow-2xl">
          {step === 'email' ? (
            <form onSubmit={handleEmail} className="space-y-4" data-testid="edu-login-email-form">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  data-testid="edu-login-email"
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button
                data-testid="edu-login-email-submit"
                type="submit"
                disabled={loading || !email}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white font-medium py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4" data-testid="edu-login-otp-form">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  data-testid="edu-login-otp"
                  type="text"
                  required
                  maxLength={6}
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-lg text-white text-center tracking-widest font-mono placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors"
                  autoFocus
                />
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button
                data-testid="edu-login-otp-submit"
                type="submit"
                disabled={loading || otp.length < 6}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white font-medium py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Sign In'}
              </button>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <button type="button" onClick={() => { setStep('email'); setOtp(''); setError(''); }} className="hover:text-gray-300 transition-colors">
                  ← Change email
                </button>
                <button type="button" onClick={handleResend} disabled={resendCooldown > 0} className="hover:text-purple-400 disabled:opacity-50 transition-colors">
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
